import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Mail, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/submissions?limit=500");
      setItems(r.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyAllEmails = () => {
    const emails = [...new Set(items.map((i) => i.email).filter(Boolean))];
    navigator.clipboard.writeText(emails.join(", "));
    toast.success(`${emails.length} unique emails copied`);
  };

  const filtered = q
    ? items.filter((i) =>
        (i.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (i.submitter_name || "").toLowerCase().includes(q.toLowerCase()) ||
        (i.text || "").toLowerCase().includes(q.toLowerCase())
      )
    : items;

  const uniqueEmails = new Set(items.map((i) => i.email).filter(Boolean)).size;
  const totalEvents = items.reduce((sum, i) => sum + (i.events_created || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-submissions-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Event submissions</h1>
          <p className="text-sm text-slate-500">
            {items.length} submission{items.length === 1 ? "" : "s"} · {uniqueEmails} unique emails ·
            {" "}{totalEvents} event{totalEvents === 1 ? "" : "s"} created
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            className="w-64"
            placeholder="Search email, name, or text…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="submissions-search"
          />
          <Button
            variant="outline"
            onClick={copyAllEmails}
            data-testid="copy-emails-btn"
          >
            <Copy size={14} className="mr-1" /> Copy all emails
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-500 text-sm">No submissions yet.</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Submitted</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Details</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Events</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`submission-row-${s.id}`}>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                    {(s.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2">
                    <a
                      href={`mailto:${s.email}`}
                      className="text-[#1A3C5A] hover:underline inline-flex items-center gap-1"
                    >
                      <Mail size={12} /> {s.email}
                    </a>
                  </td>
                  <td className="px-3 py-2">{s.submitter_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="text-slate-700 line-clamp-2">{s.text}</div>
                    {s.image_url && (
                      <a href={s.image_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1 mt-1">
                        <ExternalLink size={10} /> image
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        s.status === "processed"
                          ? "text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded text-xs"
                          : s.status === "no_event_found" || s.status === "extraction_failed"
                          ? "text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-xs"
                          : "text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-xs"
                      }
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">{s.events_created || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
